import useMutation from 'hooks/react-query/useMutation';
import useQueryClient from 'hooks/react-query/useClientQuery';

import { useCallback } from 'react';
import verifyBeneficiary, {
  type VerifyBeneficiaryBody,
  type VerifyBeneficiaryResponse,
  VerifyBeneficiaryStatus,
} from 'api/v2/transactions/verifyBeneficiary';

export interface BeneficiaryVerificationResult {
  data: VerifyBeneficiaryResponse;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
}

export interface UseBeneficiaryVerificationOptions {
  onSuccess?: (data: VerifyBeneficiaryResponse) => void;
  onError?: (error: Error) => void;
  cacheTime?: number;
  staleTime?: number;
}

export interface UseBeneficiaryVerificationReturn {
  verifyBeneficiary: (
    params: VerifyBeneficiaryBody,
  ) => Promise<VerifyBeneficiaryResponse>;
  isLoading: boolean;
  error: Error | null;
  data: VerifyBeneficiaryResponse | undefined;
  reset: () => void;
}

/**
 * Hook for verifying beneficiary information with caching and error handling
 * Provides verification status, token, and name suggestions
 */
export const useBeneficiaryVerification = (
  options: UseBeneficiaryVerificationOptions = {},
): UseBeneficiaryVerificationReturn => {
  const queryClient = useQueryClient();
  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: verifyBeneficiary,
    onSuccess: (data) => {
      // Cache the verification result
      const queryKey = ['beneficiaryVerification', data.name, data.iban];
      queryClient.setQueryData(queryKey, data, {
        updatedAt: Date.now(),
      });

      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  const verifyBeneficiaryWithCache = useCallback(
    async (
      params: VerifyBeneficiaryBody,
    ): Promise<VerifyBeneficiaryResponse> => {
      const { name, iban } = params;
      const queryKey = ['beneficiaryVerification', name, iban];

      // Check for cached data first
      const cachedData =
        queryClient.getQueryData<VerifyBeneficiaryResponse>(queryKey);

      if (cachedData) {
        // Check if cached data is still valid (not expired)
        const now = new Date();
        const expiresAt = new Date(cachedData.expiresAt);

        if (expiresAt > now) {
          onSuccess?.(cachedData);
          return cachedData;
        } else {
          // Remove expired data from cache
          queryClient.removeQueries(queryKey);
        }
      }

      return mutation.mutateAsync(params);
    },
    [mutation, queryClient, onSuccess],
  );

  return {
    verifyBeneficiary: verifyBeneficiaryWithCache,
    isLoading: mutation.isLoading,
    error: mutation.error as Error | null,
    data: mutation.data,
    reset: mutation.reset,
  };
};

export { VerifyBeneficiaryStatus };
